/**
 * ─────────────────────────────────────────────────────────────
 * SITE CONFIG — static, developer-owned settings.
 *
 * This is the compile-time layer: brand, contact, currency, and
 * *default* opening hours / services. Things the business owner needs
 * to change without a redeploy live in the editable content layer
 * instead (see config/content.registry.ts + lib/content.ts).
 *
 * Rule of thumb:
 *   - changes rarely, set by the developer  → here
 *   - changes often, edited by the owner     → content collection
 * ─────────────────────────────────────────────────────────────
 */
import type { OpeningHour } from '@/lib/types';

export interface SiteConfig {
  name: string;
  legalName: string;
  tagline: string;
  description: string;
  /** Absolute URL of the deployed site (overridden by NEXT_PUBLIC_SITE_URL). */
  url: string;
  locale: string;

  contact: {
    email: string;
    phone: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
    geo?: { lat: number; lng: number };
  };

  social: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };

  /** Brand palette — fed straight into the MUI theme. */
  brand: {
    primary: string;
    secondary: string;
    mode: 'light' | 'dark';
  };

  /** Default opening hours. The owner can override these live via the
   *  `hours` content slot; use resolveHours() to read the effective value. */
  openingHours: OpeningHour[];

  /** Appointment booking defaults. */
  booking: {
    slotMinutes: number;
    horizonDays: number;
    services: string[];
  };

  /** Currency for product prices (ISO 4217). */
  currency: string;
}

export const siteConfig: SiteConfig = {
  name: 'Les Délices de Tunis',
  legalName: 'Les Délices de Tunis',
  tagline: 'Pâtisserie orientale maison — miel, amandes & fleur d’oranger',
  description:
    'Pâtisserie orientale maison à Grenoble : baklava, kalb el louz, cornes de gazelle, zalabia… Des douceurs tunisiennes préparées chaque jour, à déguster sur place ou à emporter. 110 Cours Berriat, Grenoble.',
  url: process.env.NEXT_PUBLIC_SITE_URL || 'https://les-delices-de-tunis-patisserie-oriental.boite-a-site.fr',
  locale: 'fr_FR',

  contact: {
    email: '',
    phone: '+33 9 83 30 79 28',
    address: '110 Cours Berriat',
    city: 'Grenoble',
    postalCode: '38000',
    country: 'FR',
    geo: { lat: 45.1885578, lng: 5.7117429 },
  },

  social: {
    facebook:
      'https://www.facebook.com/p/P%C3%A2tisserie-les-d%C3%A9lices-de-Tunis-100054871955344/',
  },

  brand: {
    // Miel/caramel profond pour les CTA, pistache en accent : la palette d'une
    // pâtisserie orientale (miel, amande, pistache, fleur d’oranger).
    primary: '#A95C1A',
    secondary: '#5E7A33',
    mode: 'light',
  },

  // Ouvert 7 j/7 de 9 h à 21 h (horaires relevés sur les annuaires).
  openingHours: [
    { day: 1, open: '09:00', close: '21:00' },
    { day: 2, open: '09:00', close: '21:00' },
    { day: 3, open: '09:00', close: '21:00' },
    { day: 4, open: '09:00', close: '21:00' },
    { day: 5, open: '09:00', close: '21:00' },
    { day: 6, open: '09:00', close: '21:00' },
    { day: 0, open: '09:00', close: '21:00' },
  ],

  booking: {
    slotMinutes: 30,
    horizonDays: 30,
    services: [],
  },

  currency: 'EUR',
};

export default siteConfig;
